import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { useSystemHealthSummary } from '../../../system-health/useSystemHealthSummary';
import MetricSparkline from '../../../system-health/components/MetricSparkline';
import { systemHealthLinkPath } from '../../../system-health/utils';
import { skeletonPulseClass, viewAllLinkClass, widgetCardClass, widgetTitleClass } from '../../themeHelpers';

export interface SystemHealthResourcesWidgetProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const SystemHealthResourcesWidget: React.FC<SystemHealthResourcesWidgetProps> = ({ embedded }) => {
  const { colors } = useTheme();
  const { can } = useAuth();
  const healthLink = systemHealthLinkPath(can('system_health', 'edit'));
  const { data, isLoading, error } = useSystemHealthSummary();
  const cardClass = widgetCardClass(colors, embedded);

  const diskPoints = useMemo(
    () => (data?.trends?.disk ?? []).map((p) => p.usedPercent),
    [data?.trends?.disk],
  );
  const memoryPoints = useMemo(
    () => (data?.trends?.memory ?? []).map((p) => p.usedPercent),
    [data?.trends?.memory],
  );

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`${widgetTitleClass(colors)} mb-3`}>Resource trends</h2>
        <div className={`animate-pulse h-32 rounded-lg ${skeletonPulseClass(colors)}`} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cardClass}>
        <h2 className={widgetTitleClass(colors)}>Resource trends</h2>
        <p className={`text-sm mt-3 ${colors.textMuted}`}>Unable to load resource trends.</p>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-3 flex-shrink-0 gap-2">
        <h2 className={widgetTitleClass(colors)}>Resource trends</h2>
        <Link to={healthLink} className={viewAllLinkClass(colors)}>
          Details
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <MetricSparkline
          label="Disk usage"
          points={diskPoints}
          colorClass={
            data.disk.usedPercent >= 90
              ? colors.error
              : data.disk.usedPercent >= 80
                ? colors.warning
                : colors.success
          }
        />
        <MetricSparkline
          label="Memory usage"
          points={memoryPoints}
          colorClass={
            data.memory.usedPercent >= 90
              ? colors.error
              : data.memory.usedPercent >= 85
                ? colors.warning
                : colors.primaryFg
          }
        />
      </div>
    </div>
  );
};

export default SystemHealthResourcesWidget;
